import jsPDF from 'jspdf';
// import html2canvas from 'html2canvas';

interface ExportData {
  summary: {
    totalCalls: number;
    totalCost: number;
    activeEndpoints: number;
    periodDays: number;
    periodDisplay: string;
  };
  monthlyBilling: {
    monthlyUsageCalls: number;
    monthlyUsageCost: number;
    monthlyMinimumCost: number;
    totalMonthlyCost: number;
    monthlyEndpointsUsed: number;
  };
  endpoints: Array<{
    name: string;
    calls: number;
    cost: number;
    successRate?: number;
  }>;
  costBreakdown: Array<{
    name: string;
    value: number;
    displayValue: number;
    calls?: number;
    avg_cost?: number;
  }>;
  chartElement?: HTMLElement | null;
  pieChartElement?: HTMLElement | null;
  userName?: string;
  userEmail?: string;
}

export const generateUsageReportPDF = async (data: ExportData) => {
  const pdf = new jsPDF('p', 'mm', 'a4');
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 20;
  const contentWidth = pageWidth - (margin * 2);
  let currentY = margin;

  // Helper function to check if we need a new page
  const checkNewPage = (requiredSpace: number) => {
    if (currentY + requiredSpace > pageHeight - margin) {
      pdf.addPage();
      currentY = margin;
      return true;
    }
    return false;
  };

  // Helper function to format numbers
  const fmtNum = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toLocaleString();
  };

  const fmtUSD = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Set fonts and colors (simple black text)
  pdf.setFont('helvetica');
  pdf.setTextColor(0, 0, 0); // Black text

  // Title
  pdf.setFontSize(20);
  pdf.text('Usage Report', margin, currentY);
  currentY += 10;
  
  // Date and user info
  pdf.setFontSize(12);
  const date = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });
  pdf.text(`Generated on ${date}`, margin, currentY);
  currentY += 6;
  
  if (data.userName || data.userEmail) {
    const userInfo = data.userName || data.userEmail || '';
    pdf.text(`User: ${userInfo}`, margin, currentY);
    currentY += 6;
  }
  
  pdf.text(`Period: ${data.summary.periodDisplay}`, margin, currentY);
  currentY += 15;

  // Summary Section
  pdf.setFontSize(16);
  pdf.text('SUMMARY', margin, currentY);
  currentY += 10;
  
  pdf.setFontSize(12);
  pdf.text(`Total API Calls (${data.summary.periodDisplay}): ${fmtNum(data.summary.totalCalls)}`, margin, currentY);
  currentY += 6;
  pdf.text(`Total Cost (${data.summary.periodDisplay}): ${fmtUSD(data.summary.totalCost)}`, margin, currentY);
  currentY += 6;
  pdf.text(`Active Endpoints: ${data.summary.activeEndpoints}`, margin, currentY);
  currentY += 15;

  // Monthly Billing Section
  pdf.setFontSize(16);
  pdf.text('MONTHLY BILLING', margin, currentY);
  currentY += 10;
  
  pdf.setFontSize(12);
  pdf.text(`Monthly Usage: ${fmtNum(data.monthlyBilling.monthlyUsageCalls)} calls`, margin, currentY);
  currentY += 6;
  pdf.text(`Usage Cost: ${fmtUSD(data.monthlyBilling.monthlyUsageCost)} ($0.10 per call)`, margin, currentY);
  currentY += 6;
  pdf.text(`Endpoints Used: ${data.monthlyBilling.monthlyEndpointsUsed}`, margin, currentY);
  currentY += 6;
  pdf.text(`Minimum Cost: ${fmtUSD(data.monthlyBilling.monthlyMinimumCost)} ($20 per endpoint)`, margin, currentY);
  currentY += 6;
  
  // Total Monthly Cost - highlighted
  pdf.setFontSize(14);
  pdf.text(`TOTAL MONTHLY COST: ${fmtUSD(data.monthlyBilling.totalMonthlyCost)}`, margin, currentY);
  currentY += 15;

  // Cost Breakdown Section
  checkNewPage(30);
  pdf.setFontSize(16);
  pdf.text('COST BREAKDOWN BY ENDPOINT', margin, currentY);
  currentY += 10;

  pdf.setFontSize(12);
  const sortedBreakdown = [...data.costBreakdown]
    .filter(item => item.value > 0)
    .sort((a, b) => b.value - a.value);

  sortedBreakdown.forEach((item, index) => {
    if (checkNewPage(6)) {
      pdf.setFontSize(16);
      pdf.text('COST BREAKDOWN BY ENDPOINT (continued)', margin, currentY);
      currentY += 10;
      pdf.setFontSize(12);
    }
    
    const line = `${index + 1}. ${item.name}: ${fmtUSD(item.value)} (${item.displayValue}%)`;
    pdf.text(line, margin, currentY);
    currentY += 6;
    
    // Add call count if available
    if (item.calls) {
      pdf.setFontSize(10);
      pdf.text(`   ${fmtNum(item.calls)} calls`, margin + 5, currentY);
      currentY += 5;
      pdf.setFontSize(12);
    }
  });

  currentY += 10;

  // Endpoint Usage Table
  checkNewPage(50);
  pdf.setFontSize(16);
  pdf.text('TOP ENDPOINTS BY USAGE', margin, currentY);
  currentY += 10;

  // Table header
  pdf.setFontSize(10);
  const headerY = currentY;
  pdf.text('Endpoint Name', margin, headerY);
  pdf.text('Calls', margin + 100, headerY);
  pdf.text('Cost', margin + 130, headerY);
  pdf.text('Success Rate', margin + 160, headerY);
  
  // Draw line under header
  pdf.line(margin, headerY + 2, margin + contentWidth, headerY + 2);
  currentY += 8;

  // Table rows
  const maxEndpoints = 20;
  data.endpoints.slice(0, maxEndpoints).forEach((endpoint, index) => {
    if (checkNewPage(6)) {
      // Redraw header on new page
      pdf.setFontSize(16);
      pdf.text('TOP ENDPOINTS BY USAGE (continued)', margin, currentY);
      currentY += 10;
      
      pdf.setFontSize(10);
      const headerY = currentY;
      pdf.text('Endpoint Name', margin, headerY);
      pdf.text('Calls', margin + 100, headerY);
      pdf.text('Cost', margin + 130, headerY);
      pdf.text('Success Rate', margin + 160, headerY);
      pdf.line(margin, headerY + 2, margin + contentWidth, headerY + 2);
      currentY += 8;
    }

    pdf.setFontSize(9);
    
    // Endpoint name (truncate if too long)
    let name = endpoint.name;
    if (name.length > 35) {
      name = name.substring(0, 35) + '...';
    }
    pdf.text(name, margin, currentY);
    
    // Calls
    pdf.text(fmtNum(endpoint.calls), margin + 100, currentY);
    
    // Cost
    pdf.text(fmtUSD(endpoint.cost), margin + 130, currentY);
    
    // Success Rate
    if (endpoint.successRate !== undefined) {
      pdf.text(`${endpoint.successRate.toFixed(1)}%`, margin + 160, currentY);
    } else {
      pdf.text('N/A', margin + 160, currentY);
    }
    
    currentY += 5;
  });

  // Footer
  currentY = pageHeight - 15;
  pdf.setFontSize(8);
  pdf.text('Generated by Integuru Usage Dashboard', margin, currentY);
  const pageText = `Page ${pdf.internal.pages.length}`;
  pdf.text(pageText, pageWidth - margin - pdf.getTextWidth(pageText), currentY);

  // Save the PDF
  const fileName = `usage-report-${new Date().toISOString().split('T')[0]}.pdf`;
  pdf.save(fileName);
};

// Helper function to get colors for pie chart
const getColorForIndex = (index: number): { r: number, g: number, b: number } => {
  const colors = [
    { r: 96, g: 165, b: 250 },   // blue-400
    { r: 52, g: 211, b: 153 },   // emerald-400
    { r: 251, g: 146, b: 60 },   // orange-400
    { r: 244, g: 114, b: 182 },  // pink-400
    { r: 167, g: 139, b: 250 },  // violet-400
    { r: 250, g: 204, b: 21 },   // yellow-400
    { r: 74, g: 222, b: 128 },   // green-400
    { r: 248, g: 113, b: 113 },  // red-400
  ];
  return colors[index % colors.length];
};